/**
 * DateField - Date input field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Uses native type="date" for browser date picker.
 * Value format: YYYY-MM-DD (ISO 8601 date string).
 */

import type { ChangeEvent } from 'react';
import type { DateFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import styles from './fields.module.css';

/**
 * DateField renders a native date picker input.
 */
export function DateField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: DateFieldProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // field.min / field.max are epoch ms of the UTC midnight for a bound day.
  // The native picker wants YYYY-MM-DD; omit the attribute when the bound is unset.
  const minAttr =
    field.min != null ? new Date(field.min).toISOString().slice(0, 10) : undefined;
  const maxAttr =
    field.max != null ? new Date(field.max).toISOString().slice(0, 10) : undefined;

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
    >
      <input
        type="date"
        id={field.fieldId}
        name={field.fieldId}
        className={`${styles.input} ${styles.dateInput} ${error ? styles.hasError : ''}`}
        value={value}
        min={minAttr}
        max={maxAttr}
        onChange={handleChange}
        onBlur={onBlur}
        readOnly={nonInteractive}
        tabIndex={nonInteractive ? -1 : undefined}
        style={nonInteractive ? { pointerEvents: 'none' } : undefined}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.fieldId}-error` : undefined}
      />
    </FieldWrapper>
  );
}
